import { useAccountStats } from '../stores/accountStatsStore';

/**
 * useLiquidationRisk — live margin / liquidation risk for the signed-in user.
 *
 * WHY THIS EXISTS
 * On 2026-09-08 six Delta positions were liquidated at once. The only margin
 * figure on screen at the time was "Available margin", which read ₹0 in plain
 * uncoloured text for 3h44m. Nothing else in the app knew what a liquidation was.
 *
 * The owner asked to be notified "on every tick". That is right for a number on a
 * screen and wrong for a Telegram bot, so the split is: the backend rations
 * Telegram behind a hysteresis ladder, and this hook takes every tick it is sent.
 *
 * Two sources, deliberately — both now owned by `stores/accountStatsStore`, which this
 * hook reads from (see the note above the hook itself for why they moved):
 *   • `risk:liquidation` socket events — the live path. The backend emits per
 *     evaluation (1s while an account is at risk), into the user's own room.
 *   • `GET /dashboard/liquidation-risk` — the initial paint and the reload path,
 *     because a socket only carries the NEXT tick, not the current state. Also a
 *     slow poll so a dropped socket degrades to 60s rather than to silence.
 *
 * `available: false` means NOT EVALUATED (backend just booted, or no active Delta
 * credentials). Callers must render that as unknown — never as safe.
 */

export type LiquidationSeverity = 'SAFE' | 'WATCH' | 'WARNING' | 'CRITICAL' | 'EMERGENCY';

export interface LiquidationRisk {
  available: boolean;
  broker: string;
  reason?: string | null;
  severity?: LiquidationSeverity;
  /** Maintenance-margin ratio %. The venue liquidates at 100. Null = no collateral. */
  mmr?: number | null;
  /** Initial-margin ratio %. The venue cancels open orders at 100. */
  imr?: number | null;
  /** Uniform adverse move % that would reach liquidation. */
  bufferPct?: number | null;
  netEquityUsd?: number;
  netEquityInr?: number;
  maintenanceMarginUsd?: number;
  availableBalanceUsd?: number;
  fundsRequiredUsd?: number;
  fundsRequiredInr?: number;
  reduceFraction?: number;
  thresholds?: { watch: number; warning: number; critical: number; emergency: number };
  modelUnverified?: boolean;
  partialCoverage?: boolean;
  excludedSymbols?: string[];
  reserveBreached?: boolean;
  positionCount?: number;
  stale?: boolean;
  evaluatedAt?: number;
  ok?: boolean;
}

/**
 * Both sources now live in `accountStatsStore` rather than here.
 *
 * This hook used to own a socket handler AND a 60s poll, per mounting component. With
 * RiskCockpit, LiquidationRiskBanner, the header pill and the chart-title rail all wanting
 * the figure, that would have been four sockets and four polls for one number. The store is
 * ref-counted, so they now share exactly one of each. The public API here is unchanged, so
 * every existing caller is untouched.
 */

export const SEVERITY_COLOR: Record<LiquidationSeverity, string> = {
  SAFE: '#16a34a',
  WATCH: '#ca8a04',
  WARNING: '#ea580c',
  CRITICAL: '#dc2626',
  EMERGENCY: '#b91c1c',
};

/**
 * Live margin / liquidation risk for the signed-in user.
 *
 * `null` = not resolved yet; `available: false` = NOT EVALUATED. Callers must render both
 * as unknown, NEVER as safe — that distinction is the whole point of the feature.
 */
export function useLiquidationRisk(): LiquidationRisk | null {
  return useAccountStats().risk;
}

/** Human label for the tile / banner. */
export function riskLabel(r: LiquidationRisk | null): string {
  if (!r || !r.available) return 'Not checked yet';
  if (r.mmr == null) return 'No collateral';
  return `${r.mmr.toFixed(0)}%`;
}
